import {createView, view, Link, Icon} from '@sail/ui';
import {
  InlineDatumReactComponentProps,
  useAnalytics,
} from '@stripe-internal/inline-data';
import {
  LightCard,
  ThemeProvider,
  LinkTheme,
  DefaultTheme,
  useNewUniversalHelpDesign,
} from '@stripe-internal/conversations-ui';
import {defineMessages, FormattedMessage} from 'react-intl';
import {business} from '@sail/icons/react/Icon';

export type ValueProps = {
  merchant_name: string;
  merchant_avatar: string;
  merchant_website: string;
  merchant_email: string;
  merchant_phone: string;
  audience_type: string;
};

const messages = defineMessages({
  websiteTitle: {
    id: 'mse_inline_data.universal_help_business_detail.website_title',
    description: 'Heading for business website section',
    defaultMessage: 'Website',
  },
  emailTitle: {
    id: 'mse_inline_data.universal_help_business_detail.email_title',
    description: 'Heading for business email section',
    defaultMessage: 'Email',
  },
  phoneNumberTitle: {
    id: 'mse_inline_data.universal_help_business_detail.phone_number_title',
    description: 'Heading for business phone number section',
    defaultMessage: 'Phone number',
  },
  noAdditionalBusinessDetails: {
    id: 'mse_inline_data.universal_help_business_detail.no_additional_business_details',
    description:
      'Message directing user to contact the business when no additional details available.',
    defaultMessage:
      'We do not have additional details of {merchant}, you will need to reach out to the business directly for help.',
  },
});

const MerchantWebsiteComponentOld = ({
  merchantWebsite,
  isStripeOrExpressAudience,
  hasPhoneOrEmail,
}: {
  merchantWebsite: string;
  isStripeOrExpressAudience: boolean;
  hasPhoneOrEmail: boolean;
}) => {
  return (
    <view.div
      data-testid="merchant-website"
      css={{
        font: 'label.medium',
        marginBottom: hasPhoneOrEmail ? 'medium' : '0',
      }}
    >
      <Link
        type="primary"
        href={merchantWebsite}
        target="_blank"
        rel="noopener noreferrer"
        css={{
          color: isStripeOrExpressAudience ? '#7A73FF' : 'rgb(0, 102, 53)',
          fontWeight: 'bold',
        }}
      >
        {merchantWebsite}
      </Link>
    </view.div>
  );
};

const MerchantWebsiteComponent = ({
  merchantWebsite,
  hasPhoneOrEmail,
}: {
  merchantWebsite: string;
  hasPhoneOrEmail: boolean;
}) => {
  return (
    <view.div
      data-testid="merchant-website"
      css={{
        font: 'label.medium',
        marginBottom: hasPhoneOrEmail ? 'medium' : '0',
      }}
    >
      <Link
        type="secondary"
        href={merchantWebsite}
        target="_blank"
        rel="noopener noreferrer"
      >
        {merchantWebsite}
      </Link>
    </view.div>
  );
};

type MerchantContactInfoComponentProps = {
  type: 'email' | 'phone';
  value: string;
  isStripeOrExpressAudience?: boolean;
};

const MerchantContactInfoComponentOld = createView(
  ({
    type,
    value,
    isStripeOrExpressAudience,
    ...props
  }: MerchantContactInfoComponentProps) => {
    const titleMessage =
      type === 'email' ? messages.emailTitle : messages.phoneNumberTitle;
    const href = type === 'email' ? `mailto:${value}` : `tel:${value}`;
    const id = `merchant-${type}`;
    return (
      <view.div css={{stack: 'y', gap: 'xsmall'}}>
        <view.div css={{font: 'label.medium.emphasized'}}>
          <FormattedMessage {...titleMessage} />
        </view.div>
        <view.div data-testid={id} css={{font: 'label.medium'}} {...props}>
          <Link
            type="primary"
            href={href}
            css={{
              color: isStripeOrExpressAudience ? '#596171' : 'rgb(0, 102, 53)',
              fontWeight: isStripeOrExpressAudience ? 'normal' : 'bold',
            }}
          >
            {value}
          </Link>
        </view.div>
      </view.div>
    );
  },
);

const MerchantContactInfoComponent = createView(
  ({type, value, ...props}: MerchantContactInfoComponentProps) => {
    const titleMessage =
      type === 'email' ? messages.emailTitle : messages.phoneNumberTitle;
    const href = type === 'email' ? `mailto:${value}` : `tel:${value}`;
    const id = `merchant-${type}`;
    return (
      <view.div css={{stack: 'y', gap: 'xxsmall'}}>
        <view.div css={{font: 'label.medium.emphasized'}}>
          <FormattedMessage {...titleMessage} />
        </view.div>
        <view.div data-testid={id} css={{font: 'label.medium'}} {...props}>
          <Link type="secondary" href={href}>
            {value}
          </Link>
        </view.div>
      </view.div>
    );
  },
);

const BusinessDetailsOld = ({
  merchantPropsValue,
}: {
  merchantPropsValue: ValueProps;
}) => {
  const analytics = useAnalytics();
  const {
    merchant_email: merchantEmail,
    merchant_phone: merchantPhone,
    merchant_website: merchantWebsite,
    audience_type: audienceType,
  } = merchantPropsValue;

  const isStripeOrExpressAudience =
    audienceType === 'stripe' || audienceType === 'express';
  const hasPhoneOrEmail = merchantPhone || merchantEmail;

  analytics.viewed(`viewed_${audienceType}_business_card_detail`);
  return (
    <view.div>
      {!isStripeOrExpressAudience && merchantWebsite && (
        <>
          <view.div css={{font: 'label.medium.emphasized'}}>
            <FormattedMessage {...messages.websiteTitle} />
          </view.div>
          <MerchantWebsiteComponentOld
            merchantWebsite={merchantWebsite}
            isStripeOrExpressAudience={isStripeOrExpressAudience}
            hasPhoneOrEmail={!!hasPhoneOrEmail}
          />
        </>
      )}

      {hasPhoneOrEmail &&
        (isStripeOrExpressAudience ? (
          <view.div
            css={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 'large',
            }}
          >
            {merchantPhone && (
              <>
                <view.div css={{flex: '1 1 auto'}}>
                  <MerchantContactInfoComponentOld
                    type="phone"
                    value={merchantPhone}
                    isStripeOrExpressAudience={isStripeOrExpressAudience}
                  />
                </view.div>
              </>
            )}
            {merchantEmail && (
              <>
                <view.div css={{flex: '1 1 auto'}}>
                  <MerchantContactInfoComponentOld
                    type="email"
                    value={merchantEmail}
                    isStripeOrExpressAudience={isStripeOrExpressAudience}
                  />
                </view.div>
              </>
            )}
          </view.div>
        ) : (
          <>
            {merchantEmail && (
              <>
                <MerchantContactInfoComponentOld
                  type="email"
                  value={merchantEmail}
                  isStripeOrExpressAudience={isStripeOrExpressAudience}
                  css={{marginBottom: hasPhoneOrEmail ? 'medium' : '0'}}
                />
              </>
            )}
            {merchantPhone && (
              <>
                <MerchantContactInfoComponentOld
                  type="phone"
                  value={merchantPhone}
                  isStripeOrExpressAudience={isStripeOrExpressAudience}
                  css={{marginBottom: 'medium'}}
                />
              </>
            )}
          </>
        ))}
    </view.div>
  );
};

const BusinessDetails = ({
  merchantPropsValue,
}: {
  merchantPropsValue: ValueProps;
}) => {
  const analytics = useAnalytics();
  const {
    merchant_email: merchantEmail,
    merchant_phone: merchantPhone,
    merchant_website: merchantWebsite,
    audience_type: audienceType,
  } = merchantPropsValue;

  const isStripeOrExpressAudience =
    audienceType === 'stripe' || audienceType === 'express';
  const hasPhoneOrEmail = merchantPhone || merchantEmail;

  analytics.viewed(`viewed_${audienceType}_business_card_detail`);
  return (
    <view.div>
      {!isStripeOrExpressAudience && merchantWebsite ? (
        <MerchantWebsiteComponent
          merchantWebsite={merchantWebsite}
          hasPhoneOrEmail={!!hasPhoneOrEmail}
        />
      ) : null}

      {hasPhoneOrEmail ? (
        <view.div
          css={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'small',
          }}
        >
          {merchantPhone && (
            <>
              <view.div css={{flex: '1 1 auto'}}>
                <MerchantContactInfoComponent
                  type="phone"
                  value={merchantPhone}
                />
              </view.div>
            </>
          )}
          {merchantEmail && (
            <>
              <view.div css={{flex: '1 1 auto'}}>
                <MerchantContactInfoComponent
                  type="email"
                  value={merchantEmail}
                />
              </view.div>
            </>
          )}
        </view.div>
      ) : null}
    </view.div>
  );
};

const BusinessCardDetailOld = ({
  props,
}: {
  props: InlineDatumReactComponentProps;
}) => {
  const {datum} = props;
  const merchantPropsValue = datum.value as ValueProps;
  const {
    merchant_name: merchantName,
    merchant_avatar: merchantAvatar,
    merchant_website: merchantWebsite,
    merchant_phone: merchantPhone,
    merchant_email: merchantEmail,
    audience_type: audienceType,
  } = merchantPropsValue;

  const isStripeOrExpressAudience =
    audienceType === 'stripe' || audienceType === 'express';

  const hasContactInfo = merchantWebsite || merchantPhone || merchantEmail;
  const hasPhoneOrEmail = merchantPhone || merchantEmail;

  return (
    <view.div css={{gap: 'medium', stack: 'y'}}>
      <LightCard isOuter>
        <view.div css={{stack: 'y', gap: 'large'}}>
          <view.div css={{stack: 'y', gap: 'xsmall'}}>
            <view.div css={{stack: 'x', gap: 'small'}}>
              {isStripeOrExpressAudience ? (
                merchantAvatar && (
                  <view.div
                    css={{
                      stack: 'x',
                      width: 'large',
                      height: 'large',
                      alignSelfY: 'center',
                    }}
                  >
                    <view.img
                      data-testid="merchant-avatar"
                      src={merchantAvatar}
                      css={{
                        width: 'large',
                        height: 'large',
                        borderRadius: '50%',
                        border: '1px inset rgba(0, 0, 0, 0.15)',
                      }}
                    />
                  </view.div>
                )
              ) : (
                <view.div
                  css={{
                    stack: 'x',
                    width: 'large',
                    height: 'large',
                    alignSelfY: 'center',
                  }}
                >
                  <view.div
                    css={{
                      borderRadius: '50%',
                      border: '1px inset rgba(0, 0, 0, 0.15)',
                      width: 'fill',
                      height: 'fill',
                    }}
                  >
                    {merchantAvatar ? (
                      <view.img
                        data-testid="merchant-avatar"
                        src={merchantAvatar}
                        css={{
                          width: 'large',
                          height: 'large',
                          borderRadius: '50%',
                          border: '1px inset rgba(0, 0, 0, 0.15)',
                        }}
                      />
                    ) : (
                      <Icon
                        data-testid="merchant-avatar"
                        icon={business}
                        css={{
                          padding: 'xsmall',
                          width: 'fill',
                          height: 'fill',
                        }}
                      />
                    )}
                  </view.div>
                </view.div>
              )}

              <view.h3
                css={{margin: 0, font: 'heading.large'}}
                data-testid="merchant-name"
              >
                {merchantName}
              </view.h3>
            </view.div>

            {isStripeOrExpressAudience && merchantWebsite && (
              <>
                <MerchantWebsiteComponentOld
                  merchantWebsite={merchantWebsite}
                  isStripeOrExpressAudience={isStripeOrExpressAudience}
                  hasPhoneOrEmail={!!hasPhoneOrEmail}
                />
                <BusinessDetailsOld merchantPropsValue={merchantPropsValue} />
              </>
            )}
          </view.div>

          {!isStripeOrExpressAudience && hasContactInfo && (
            <>
              <BusinessDetailsOld merchantPropsValue={merchantPropsValue} />
            </>
          )}

          {!hasContactInfo && (
            <>
              <view.div css={{gap: 'medium', stack: 'y'}}>
                <view.div data-testid="no-additional-merchant-details">
                  <FormattedMessage
                    {...messages.noAdditionalBusinessDetails}
                    values={{merchant: merchantName}}
                  />
                </view.div>
              </view.div>
            </>
          )}
        </view.div>
      </LightCard>
    </view.div>
  );
};

const BusinessCardDetail = ({
  props,
}: {
  props: InlineDatumReactComponentProps;
}) => {
  const {datum} = props;
  const merchantPropsValue = datum.value as ValueProps;
  const {
    merchant_name: merchantName,
    merchant_avatar: merchantAvatar,
    merchant_website: merchantWebsite,
    merchant_phone: merchantPhone,
    merchant_email: merchantEmail,
    audience_type: audienceType,
  } = merchantPropsValue;

  const isStripeOrExpressAudience =
    audienceType === 'stripe' || audienceType === 'express';

  const hasContactInfo = merchantWebsite || merchantPhone || merchantEmail;
  const hasPhoneOrEmail = merchantPhone || merchantEmail;

  return (
    <view.div css={{gap: 'medium', stack: 'y'}}>
      <LightCard isOuter>
        <view.div css={{stack: 'y', gap: 'small'}}>
          <view.div css={{stack: 'y', gap: 'xsmall'}}>
            <view.div css={{stack: 'x', gap: 'small'}}>
              {merchantAvatar ? (
                <view.div
                  css={{
                    stack: 'x',
                    width: 'large',
                    height: 'large',
                    alignSelfY: 'center',
                  }}
                >
                  <view.img
                    data-testid="merchant-avatar"
                    src={merchantAvatar}
                    css={{
                      width: 'large',
                      height: 'large',
                      borderRadius: '50%',
                      border: '1px inset rgba(0, 0, 0, 0.15)',
                    }}
                  />
                </view.div>
              ) : null}

              <view.h3
                css={{margin: 0, font: 'heading.large'}}
                data-testid="merchant-name"
              >
                {merchantName}
              </view.h3>
            </view.div>

            {isStripeOrExpressAudience && merchantWebsite && (
              <>
                <MerchantWebsiteComponent
                  merchantWebsite={merchantWebsite}
                  hasPhoneOrEmail={!!hasPhoneOrEmail}
                />
                <BusinessDetails merchantPropsValue={merchantPropsValue} />
              </>
            )}
          </view.div>

          {!isStripeOrExpressAudience && hasContactInfo && (
            <>
              <BusinessDetails merchantPropsValue={merchantPropsValue} />
            </>
          )}

          {!hasContactInfo && (
            <>
              <view.div css={{gap: 'medium', stack: 'y'}}>
                <view.div data-testid="no-additional-merchant-details">
                  <FormattedMessage
                    {...messages.noAdditionalBusinessDetails}
                    values={{merchant: merchantName}}
                  />
                </view.div>
              </view.div>
            </>
          )}
        </view.div>
      </LightCard>
    </view.div>
  );
};

const getThemeForAudience = (audience: string) => {
  switch (audience) {
    case 'stripe':
      return DefaultTheme;
    case 'link':
      return LinkTheme;
    case 'express':
      return DefaultTheme;
    default:
      return DefaultTheme;
  }
};

export const BusinessCard = (props: InlineDatumReactComponentProps) => {
  const {datum} = props;
  const merchantPropsValue = datum.value as ValueProps;
  const audienceType = merchantPropsValue?.audience_type || '';
  const useNewDesign = useNewUniversalHelpDesign();

  return (
    <ThemeProvider value={getThemeForAudience(audienceType)}>
      {useNewDesign ? (
        <BusinessCardDetail props={props} />
      ) : (
        <BusinessCardDetailOld props={props} />
      )}
    </ThemeProvider>
  );
};
